// File: frontend/src/pages/Reinsurance/contexts/CedentContext.js

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { reinsuranceService } from '../../../services/reinsurance';

const CedentContext = createContext();

export const useCedent = () => useContext(CedentContext);

export const CedentProvider = ({ children }) => {
  const [cedents, setCedents] = useState([]);
  const [currentCedent, setCurrentCedent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Track the last used filters to prevent redundant API calls
  const lastFilters = useRef({});
  // Track if we've already loaded data
  const hasLoadedData = useRef(false);

  const fetchCedents = useCallback(async (filters = {}) => {
    // Check if we're fetching with the same filters as last time
    const filtersString = JSON.stringify(filters);
    const lastFiltersString = JSON.stringify(lastFilters.current);
    
    // Only fetch if filters have changed or we don't have data yet
    if (filtersString !== lastFiltersString || !hasLoadedData.current) {
      setLoading(true);
      try {
        const response = await reinsuranceService.listCedents(filters);
        
        if (response.success) {
          setCedents(response.data);
          // Update last used filters
          lastFilters.current = filters;
          hasLoadedData.current = true;
          return response.data;
        } else {
          setError(response.message || 'Failed to fetch cedents');
          return [];
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching cedents');
        return [];
      } finally {
        setLoading(false);
      }
    }
    
    // If filters haven't changed and we have data, just return the existing data
    return cedents;
  }, [cedents]);

  const fetchCedentById = useCallback(async (cedentId) => {
    // Don't fetch if we already have this cedent loaded
    if (currentCedent && currentCedent.cedent_id === cedentId) {
      return currentCedent;
    }
    
    setLoading(true);
    try {
      const response = await reinsuranceService.getCedent(cedentId);
      
      if (response.success) {
        setCurrentCedent(response.data);
        return response.data;
      } else {
        setError(response.message || 'Failed to fetch cedent details');
        return null;
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching cedent details');
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentCedent]);

  const createCedent = useCallback(async (cedentData) => {
    setLoading(true);
    try {
      const response = await reinsuranceService.createCedent(cedentData);
      
      if (response.success) {
        // Update the local state without making another API call
        setCedents(prev => [...prev, response.data]);
        hasLoadedData.current = true;
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to create cedent');
        return { success: false, error: response.message };
      }
    } catch (err) {
      setError(err.message || 'An error occurred while creating cedent');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCedent = useCallback(async (cedentId, cedentData) => {
    setLoading(true);
    try {
      const response = await reinsuranceService.updateCedent(cedentId, cedentData);
      
      if (response.success) {
        // Update local state without making another API call
        if (currentCedent && currentCedent.cedent_id === cedentId) {
          setCurrentCedent(response.data);
        }
        
        setCedents(prev => 
          prev.map(cedent => 
            cedent.cedent_id === cedentId ? response.data : cedent
          )
        );
        
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to update cedent');
        return { success: false, error: response.message };
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating cedent');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [currentCedent]);

  const value = {
    cedents,
    currentCedent,
    loading,
    error,
    fetchCedents,
    fetchCedentById,
    createCedent,
    updateCedent,
    clearCurrentCedent: () => setCurrentCedent(null),
    clearError: () => setError(null)
  };

  return (
    <CedentContext.Provider value={value}>
      {children}
    </CedentContext.Provider>
  );
};